import React, { useState } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { RoleComponentGuard } from "components/guards";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { RoleName } from "constants/index";

import { DataExtractionSource } from "../DataExtractionModal";

interface Props {
  fileIds: number[];
  fileNames: Record<number, string>;
  currentIndex: number;
  source?: DataExtractionSource;
  dealId?: number;
  dealName?: string;
  sectionId?: number;
  sectionTitle?: string;
  fileName?: string;
  showExtractData: boolean;
  readOnly?: boolean;
  onToggleMode: () => void;
  onPrev: () => void;
  onNext: () => void;
  onSelectFile: (index: number) => void;
  onClose: () => void;
}

export const DataExtractionHeader: React.FC<Props> = ({
  fileIds,
  fileNames,
  currentIndex,
  fileName,
  showExtractData,
  readOnly,
  onToggleMode,
  onPrev,
  onNext,
  onSelectFile,
  onClose
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const hasMultipleFiles = fileIds.length > 1;

  return (
    <Stack direction="row" alignItems="center" gap="20px" sx={{ px: "16px" }}>
      <Stack
        direction="row"
        alignItems="center"
        gap="10px"
        {...(hasMultipleFiles && {
          onClick: (e: React.MouseEvent<HTMLDivElement>) => setMenuAnchor((prev) => (prev ? null : e.currentTarget))
        })}
        sx={{ flex: 1, minWidth: 0, cursor: hasMultipleFiles ? "pointer" : "default" }}
      >
        <MaterialTooltip isShow title={fileName || ""} disableHoverListener={!fileName || fileName.length <= 30}>
          <Typography
            noWrap
            sx={{
              fontSize: "24px",
              fontWeight: 500,
              color: "#0a3142",
              lineHeight: "32px",
              maxWidth: "350px"
            }}
          >
            {fileName || "Data Extraction"}
          </Typography>
        </MaterialTooltip>

        {hasMultipleFiles && (
          <>
            <ArrowDropDownIcon sx={{ fontSize: 20 }} />
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              slotProps={{
                paper: {
                  sx: {
                    maxHeight: 300,
                    borderRadius: "8px",
                    boxShadow:
                      "0px 3px 3px -2px rgba(38,54,70,0.16), 0px 3px 4px 0px rgba(38,54,70,0.1), 0px 1px 8px 0px rgba(38,54,70,0.08)"
                  }
                }
              }}
            >
              {fileIds.map((id, idx) => (
                <MenuItem
                  key={id}
                  selected={idx === currentIndex}
                  onClick={() => {
                    onSelectFile(idx);
                    setMenuAnchor(null);
                  }}
                  sx={{
                    fontSize: "14px",
                    lineHeight: "16px",
                    color: "#071f35",
                    px: "12px",
                    py: "6px",
                    whiteSpace: "normal",
                    "&.Mui-selected": { bgcolor: "#dbe8f5" },
                    "&.Mui-selected:hover": { bgcolor: "#dbe8f5" }
                  }}
                >
                  {fileNames[id] || `File ${idx + 1}`}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Stack>

      <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
        {!readOnly && (
          <Stack
            direction="row"
            sx={{
              height: "32px",
              border: "1px solid #adbbc9",
              borderRadius: "20px",
              flexShrink: 0,
              overflow: "hidden"
            }}
          >
            <Box
              onClick={showExtractData ? onToggleMode : undefined}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pl: "12px",
                pr: "16px",
                cursor: showExtractData ? "pointer" : "default",
                bgcolor: !showExtractData ? "#071f35" : "white",
                color: !showExtractData ? "white" : "#071f35",
                fontSize: "16px",
                fontWeight: 400,
                lineHeight: "24px",
                whiteSpace: "nowrap",
                userSelect: "none"
              }}
            >
              View
            </Box>
            <Box
              onClick={!showExtractData ? onToggleMode : undefined}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pl: "16px",
                pr: "12px",
                cursor: !showExtractData ? "pointer" : "default",
                bgcolor: showExtractData ? "#071f35" : "white",
                color: showExtractData ? "white" : "#071f35",
                borderLeft: !showExtractData ? "1px solid #7b92a9" : "none",
                fontSize: "16px",
                fontWeight: 400,
                lineHeight: "24px",
                whiteSpace: "nowrap",
                userSelect: "none"
              }}
            >
              Extract Data
            </Box>
          </Stack>
        )}
      </RoleComponentGuard>

      <Stack direction="row" alignItems="center" gap="20px" justifyContent="flex-end" sx={{ flex: 1 }}>
        {hasMultipleFiles && (
          <>
            <Stack direction="row" alignItems="center" gap="10px">
              <IconButton
                onClick={onPrev}
                disabled={currentIndex <= 0}
                sx={{ padding: "6px", border: "1px solid #7b92a9", borderRadius: "32px" }}
              >
                <ChevronLeftIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Typography noWrap sx={{ fontSize: "16px", color: "#071f35", whiteSpace: "nowrap" }}>
                {currentIndex + 1} of {fileIds.length} files
              </Typography>
              <IconButton
                onClick={onNext}
                disabled={currentIndex >= fileIds.length - 1}
                sx={{ padding: "6px", border: "1px solid #7b92a9", borderRadius: "32px" }}
              >
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Stack>
            <Box sx={{ width: "1px", alignSelf: "stretch", bgcolor: "#dbe8f5" }} />
          </>
        )}
        <IconButton onClick={onClose} sx={{ padding: "4px" }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>
    </Stack>
  );
};
